import { useState, useRef, type MutableRefObject } from 'react';
import { singletonHook } from 'react-singleton-hook';

import { _getLogger } from '@doxyme/analytics';
import {
  type ExtensionRegistry,
  type ExtensionBridge,
  logErrorAndThrowBind,
  type LogErrorAndThrowBound,
} from '@doxyme/extensions-bridge';
import { type ApplicationForPatient } from '@doxyme/extensions-graphql-types';

const logger = _getLogger('useRegistryForPatient', {
  component: 'extensions',
  lib: 'bridge',
});
const logErrorAndThrow: LogErrorAndThrowBound = logErrorAndThrowBind(logger);

const checkRef = <T>(ref: MutableRefObject<T | undefined>, name: string): T => {
  if (!Boolean(ref.current)) {
    logErrorAndThrow(
      `[useRegistrySingleton] ${name} dependency has not been set`,
    );
  }
  return ref.current as T;
};

export type UseRegistryForPatientReturnValue = {
  apps: ApplicationForPatient[];
  isLoaded: boolean;
  setDependencies: (
    extensionBridge: ExtensionBridge,
    extensionRegistry: ExtensionRegistry,
    tenantId?: string,
  ) => void;
  refresh: () => Promise<void>;
  isInitialized: boolean;
};

const hookInitValues = {
  apps: [],
  isLoaded: false,
  setDependencies: (): void => undefined,
  refresh: async (): Promise<void> => undefined,
  isInitialized: false,
};

function useRegistryForPatientImpl(): UseRegistryForPatientReturnValue {
  const [apps, setApps] = useState<ApplicationForPatient[]>([]);

  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  const bridgeRef = useRef<ExtensionBridge>(undefined);
  const registryRef = useRef<ExtensionRegistry>(undefined);
  const tenantIdRef = useRef<string>(undefined);

  const setDependencies = (
    extensionBridge: ExtensionBridge,
    extensionRegistry: ExtensionRegistry,
    tenantId?: string,
  ): void => {
    bridgeRef.current = extensionBridge;
    registryRef.current = extensionRegistry;
    tenantIdRef.current = tenantId;
  };

  const updateBridge = (
    bridge: ExtensionBridge,
    updatedApps: ApplicationForPatient[],
  ): void => {
    const manifests = apps.map(({ manifest }) => manifest);
    const updatedManifests = updatedApps.map(({ manifest }) => manifest);

    for (const m of manifests) {
      if (!updatedManifests.includes(m)) {
        bridge.removeInstance(m.id);
      }
    }

    for (const m of updatedManifests) {
      if (!manifests.includes(m)) {
        bridge.addInstance(m);
      }
    }
  };

  const retrieveData = async (): Promise<void> => {
    const registry = checkRef<ExtensionRegistry>(registryRef, 'Registry');
    const bridge = checkRef(bridgeRef, 'Bridge');

    if (!tenantIdRef.current) {
      logger.warn('[useRegistrySingleton] tenantId not set, skipping refresh');
      return;
    }

    const updatedApps = await registry.getApplicationsForPatient({
      tenantId: tenantIdRef.current,
    });

    updateBridge(bridge, updatedApps);

    setApps(updatedApps);

    setIsLoaded(true);
  };

  return {
    apps,
    isLoaded,
    setDependencies,
    refresh: retrieveData,
    isInitialized: true,
  };
}

export const useRegistryForPatientSingleton =
  singletonHook<UseRegistryForPatientReturnValue>(
    hookInitValues,
    useRegistryForPatientImpl,
  );
